import { queryOptions, useQueries, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { api } from "@/lib/api";
import type {
  Product,
  ProductSchema,
  SchemaLabel,
  SchemaLabelGroup,
  SchemaStatus,
  SchemaType,
  Space,
  User,
} from "./types";

/**
 * Schema catalog: spaces, products, users and every visible product's schema, merged into lookup
 * maps. Views read names, icons and categories from here (never from denormalized copies on
 * items), so a status or label renamed elsewhere shows up everywhere at once.
 */

/**
 * The space tree including archived spaces and products (`archived=include`), so direct links to archived
 * things still resolve. The Catalog keeps them out of `spaces` / `products` (see buildCatalog); only lookups
 * by key or id and the sidebar's "Show archived" see them.
 */
export const spacesQuery = queryOptions({
  queryKey: ["spaces"],
  queryFn: ({ signal }) => api.get<Space[]>("/api/v1/spaces?archived=include", signal),
  staleTime: 60_000,
});

export const usersQuery = queryOptions({
  queryKey: ["users"],
  queryFn: ({ signal }) => api.get<User[]>("/api/v1/users", signal),
  staleTime: 5 * 60_000,
});

export const schemaQuery = (productKey: string) =>
  queryOptions({
    queryKey: ["schema", productKey.toUpperCase()],
    queryFn: ({ signal }) => api.get<ProductSchema>(`/api/v1/products/${productKey}/schema`, signal),
    staleTime: 5 * 60_000,
  });

export function useSpaces() {
  return useQuery(spacesQuery);
}

export function useUsers() {
  return useQuery(usersQuery);
}

export interface Catalog {
  /** Active (not archived) spaces, each with its active products only: what lists, pickers and commands cover. */
  spaces: Space[];
  /** Active products of the active spaces. */
  products: Product[];
  /** Every space including archived ones, each with all its products (sidebar "Show archived", direct links). */
  allSpaces: Space[];
  /** Every product including archived ones. */
  allProducts: Product[];
  /** Lookups cover archived spaces and products too, so a direct link to one still resolves. */
  productById: Map<string, Product>;
  productByKey: Map<string, Product>;
  spaceById: Map<string, Space>;
  spaceByKey: Map<string, Space>;
  /** The product is archived (or its space is): read-only, hidden from lists. */
  isArchived: (productId: string | null | undefined) => boolean;
  /** Item types, highest level first (Initiative, Epic, ..., Sub-task). */
  types: SchemaType[];
  typeById: Map<string, SchemaType>;
  statusById: Map<string, SchemaStatus>;
  /** Statuses of a workflow in position order. */
  statusesOf: (workflowId: string) => SchemaStatus[];
  labelGroups: SchemaLabelGroup[];
  groupById: Map<string, SchemaLabelGroup>;
  labels: SchemaLabel[];
  labelById: Map<string, SchemaLabel>;
  milestones: ProductSchema["milestones"];
  milestoneById: Map<string, ProductSchema["milestones"][number]>;
  users: User[];
  userById: Map<string, User>;
  schemaOf: (productId: string) => ProductSchema | undefined;
  /** Every product's schema has loaded (or there are no products). */
  ready: boolean;
}

/**
 * Keys renamed during this session (old to new). The page that shows a product or space whose key was just changed
 * still carries the old key in its URL for a moment: lookups by the old key keep resolving until the URL follows, so
 * nothing unmounts (and the field that was just edited keeps its focus). Other tabs and old bookmarks go through the
 * server lookup instead (data/aliases.ts).
 */
const renamedKeys = { product: new Map<string, string>(), space: new Map<string, string>() };

export function rememberKeyChange(kind: "product" | "space", from: string, to: string) {
  renamedKeys[kind].set(from, to);
}

/** Adds the renamed keys of this session to a by-key lookup, following chains (A to B to C). */
function withRenamedKeys<T>(byKey: Map<string, T>, renamed: Map<string, string>): Map<string, T> {
  for (const old of renamed.keys()) {
    if (byKey.has(old)) continue;
    let key = renamed.get(old);
    for (let hops = 0; key && !byKey.has(key) && hops < 10; hops++) key = renamed.get(key);
    const found = key ? byKey.get(key) : undefined;
    if (found) byKey.set(old, found);
  }
  return byKey;
}

function buildCatalog(allSpaces: Space[], users: User[], schemas: ProductSchema[], ready: boolean): Catalog {
  const allProducts = allSpaces.flatMap((s) => s.products);
  const spaces = allSpaces
    .filter((s) => !s.archivedAt)
    .map((s) => ({ ...s, products: s.products.filter((p) => !p.archivedAt) }));
  const products = spaces.flatMap((s) => s.products);
  const productById = new Map(allProducts.map((p) => [p.id, p]));
  const typeById = new Map<string, SchemaType>();
  const statusById = new Map<string, SchemaStatus>();
  const groupById = new Map<string, SchemaLabelGroup>();
  const labelById = new Map<string, SchemaLabel>();
  const milestoneById = new Map<string, ProductSchema["milestones"][number]>();
  const byWorkflow = new Map<string, SchemaStatus[]>();
  const schemaByProduct = new Map<string, ProductSchema>();
  for (const s of schemas) {
    schemaByProduct.set(s.product.id, s);
    for (const t of s.types) typeById.set(t.id, t);
    for (const st of s.statuses) {
      if (statusById.has(st.id)) continue;
      statusById.set(st.id, st);
      byWorkflow.set(st.workflowId, [...(byWorkflow.get(st.workflowId) ?? []), st]);
    }
    for (const g of s.labelGroups) groupById.set(g.id, g);
    for (const l of s.labels) labelById.set(l.id, l);
    for (const ms of s.milestones) milestoneById.set(ms.id, ms);
  }
  for (const list of byWorkflow.values()) list.sort((a, b) => a.position - b.position);
  const types = [...typeById.values()].sort((a, b) => b.level - a.level || a.position - b.position);
  return {
    spaces,
    products,
    allSpaces,
    allProducts,
    productById,
    productByKey: withRenamedKeys(new Map(allProducts.map((p) => [p.key, p])), renamedKeys.product),
    spaceById: new Map(allSpaces.map((s) => [s.id, s])),
    spaceByKey: withRenamedKeys(new Map(allSpaces.map((s) => [s.key, s])), renamedKeys.space),
    isArchived: (id) => {
      const p = id ? productById.get(id) : undefined;
      return !!p && !!p.archivedAt;
    },
    types,
    typeById,
    statusById,
    statusesOf: (wf) => byWorkflow.get(wf) ?? [],
    labelGroups: [...groupById.values()].sort((a, b) => a.position - b.position || a.slug.localeCompare(b.slug)),
    groupById,
    labels: [...labelById.values()].sort((a, b) => a.position - b.position || a.slug.localeCompare(b.slug)),
    labelById,
    milestones: [...milestoneById.values()],
    milestoneById,
    users,
    userById: new Map(users.map((u) => [u.id, u])),
    schemaOf: (id) => schemaByProduct.get(id),
    ready,
  };
}

const EMPTY: Space[] = [];
const NO_USERS: User[] = [];

export type CatalogState = { catalog: Catalog; isPending: boolean; isError: boolean; refetch: () => void };

/**
 * Loads the merged catalog for every visible product. Schemas load in parallel and are cached for
 * five minutes; with no products the catalog is ready immediately. Mounted once by
 * CatalogProvider; components read it with useCatalog().
 */
export function useLoadCatalog(): CatalogState {
  const spacesQ = useSpaces();
  const usersQ = useUsers();
  const spaces = spacesQ.data ?? EMPTY;
  const keys = useMemo(() => spaces.flatMap((s) => s.products.map((p) => p.key)), [spaces]);
  const schemas = useQueries({
    queries: keys.map((k) => schemaQuery(k)),
    combine: (results) => ({
      data: results.map((r) => r.data).filter((d): d is ProductSchema => !!d),
      stamp: results.map((r) => r.dataUpdatedAt).join(),
      pending: results.some((r) => r.isPending),
      error: results.some((r) => r.isError),
      refetch: () => {
        for (const r of results) if (r.isError) void r.refetch();
      },
    }),
  });
  const users = usersQ.data ?? NO_USERS;
  const ready = !!spacesQ.data && !schemas.pending;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `stamp` changes whenever any schema's data does
  const catalog = useMemo(
    () => buildCatalog(spaces, users, schemas.data, ready),
    [spaces, users, ready, schemas.stamp],
  );
  return {
    catalog,
    isPending: spacesQ.isPending || schemas.pending,
    isError: spacesQ.isError || schemas.error,
    refetch: () => {
      if (spacesQ.isError) void spacesQ.refetch();
      schemas.refetch();
    },
  };
}
